import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { debounce } from "lodash";

import queryString from "query-string";
import { IoFunnel } from "react-icons/io5";

import ProjectService from "../../../services/projectService";
import * as projectActions from "../../../actions/projectActions";
import * as modalActions from "../../../actions/modalActions";
import * as codeActions from "../../../actions/codeActions";

import ProjectProgressReviewListFilters from "./ProjectProgressReviewListFilters";
import ProjectProgressReviewList from "./ProjectProgressReviewList";

import Modal from "../../common/Modal";
import ConfirmModal from "../../common/ConfirmModal";
import ProjectNoteForm from "../notes/ProjectNoteForm";

import HeaderWrapper from "../../common/HeaderWrapper";

import "./ProjectProgressReviewPage.scss";

import {
  getFilterState,
  saveFilterState,
  customToast as toast
} from "../../../utils";

const ProjectProgressPage = ({
  auth,
  history,
  isMobile,
  actions,
  projectNoteToDelete,
  saving
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(
    getFilterState("PROJECT_PROGRESS_FILTERS", { page: 0, pageSize: 50 })
  );
  const [sorted, setSorted] = useState(
    getFilterState("PROJECT_PROGRESS_SORTED", [
      { id: "start_date", desc: false }
    ])
  );
  const [pages, setPages] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  // for notes
  const [selectedProject, setSelectedProject] = useState({});
  const [adding, setAdding] = useState(false);
  const [selectedNote, setSelectedNote] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const newSorted = sorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
        return acc;
      }, {});

      // Change autofilter to add Project filter on reset but not overwrite non-nulls
      //  only load projects with category "Project"
      const { codes } = await actions.loadCodes();
      const projectCategory = codes.find(
        (code) =>
          code.entity === "Project" &&
          code.field === "project_category" &&
          code.label === "Project"
      )._id;
      filters.category = filters.category || [projectCategory];

      const query = queryString.stringify(
        { ...filters, ...newSorted },
        {
          arrayFormat: "bracket"
        }
      );
      const { data: result, pages: numPages } = await ProjectService.get(
        `/list?${query}`
      );

      setData(result);
      setPages(numPages);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, sorted, actions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPages(0);
    saveFilterState("PROJECT_PROGRESS_FILTERS", filters);
  }, [filters]);
  useEffect(() => {
    saveFilterState("PROJECT_PROGRESS_SORTED", sorted);
  }, [sorted]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // fetchData();
  };

  const handleChange = (name) => (value) => {
    if (Array.isArray(value)) {
      setFilters((prev) => ({
        ...prev,
        page: 0,
        [name]: value.map((v) => v.value)
      }));
    } else {
      const newFilters = Object.keys(filters).reduce((acc, curr) => {
        if (curr !== name) {
          acc[curr] = filters[curr];
        }
        return acc;
      }, {});
      setFilters({ ...newFilters, page: 0 });
    }
  };

  const handleSearchChange = debounce((search) => {
    setFilters((prev) => ({ ...prev, page: 0, search }));
  }, 500);

  const handleSortedChange = (rtSorted) => {
    setSorted(rtSorted);
  };

  const handlePageSizeChange = (pageSize) => {
    setFilters((prev) => ({ ...prev, pageSize }));
  };
  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleReset = () => {
    setSorted([{ id: "start_date", desc: false }]);
    setFilters({
      page: 0,
      pageSize: 50,
      search: null,
      project_status: null,
      call_type: null,
      department: null,
      project_manager: null,
      customer: null,
      category: null
    });
  };

  const handleAddProject = () => {
    history.push("/app/projects/add");
  };
  const handleRowClick = (rowInfo) => {
    history.push(`/app/projects/${rowInfo.original._id}`);
  };

  const updateData = useCallback(
    async (project) => {
      const newData = [...data];
      const currIndex = newData.findIndex((p) => p._id === project._id);
      if (currIndex !== -1) {
        newData[currIndex].notes = project.notes;
        newData[currIndex].percent_complete = project.percent_complete;
      }
      setData(newData);
    },
    [data, setData]
  );

  // notes
  const onClickAddNote = async (project) => {
    await setSelectedProject(project);
    await setAdding(true);
    await setSelectedNote({});
    actions.showModalSuccess("projectDetailsModal");
  };

  const handleProgressUpdate = async (projectId) => {
    try {
      const fullProject = await ProjectService.getProject(projectId);
      updateData(fullProject);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err.message);
    }
  };
  const handleAddNote = useCallback(
    async (note) => {
      await actions.createNote(selectedProject._id, note);
      const fullProject = await ProjectService.getProject(selectedProject._id);
      updateData(fullProject);
    },
    [selectedProject, actions, updateData]
  );

  const onClickEditNote = async (noteId, project) => {
    const note = project.notes.find((n) => n._id === noteId);
    await setSelectedProject(project);
    await setAdding(false);
    await setSelectedNote(note);
    actions.showModalSuccess("projectDetailsModal");
  };

  const handleEditNote = useCallback(
    async (note) => {
      await actions.updateNote(selectedProject._id, note);
      const fullProject = await ProjectService.getProject(selectedProject._id);
      updateData(fullProject);
    },
    [selectedProject, actions, updateData]
  );

  const onClickDeleteNote = async (noteId, project) => {
    await setSelectedProject(project);
    actions.requestNoteId(noteId);
  };

  const handleDeleteNote = useCallback(async () => {
    await actions.deleteNote(selectedProject._id, projectNoteToDelete);
    const fullProject = await ProjectService.getProject(selectedProject._id);
    updateData(fullProject);
  }, [selectedProject, actions, updateData, projectNoteToDelete]);

  const noteForm = useMemo(
    () => (
      <ProjectNoteForm
        auth={auth}
        note={selectedNote}
        adding={adding}
        onAdd={handleAddNote}
        onSave={handleEditNote}
        onCancel={actions.hideModal}
        project={selectedProject}
        saving={saving}
      />
    ),
    [
      auth,
      selectedNote,
      adding,
      handleAddNote,
      handleEditNote,
      actions.hideModal,
      selectedProject,
      saving
    ]
  );

  return (
    <div className="project-review-page">
      <HeaderWrapper
        breadcrumbs={[{ label: "Project Progress Review" }]}
        onClickAdd={auth.maxRole > 2000 && handleAddProject}
        onClickAction={[
          { action: () => setShowFilters(!showFilters), icon: <IoFunnel /> }
        ]}
      />

      <ProjectProgressReviewListFilters
        show={showFilters}
        filters={filters}
        handleChange={handleChange}
        handleReset={handleReset}
        loading={loading}
        handleSearchChange={handleSearchChange}
        handleSubmit={handleSubmit}
      />

      <ProjectProgressReviewList
        data={data}
        loading={loading}
        sorted={sorted}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        pages={pages}
        pageSize={filters.pageSize}
        onRowClick={handleRowClick}
        isMobile={isMobile}
        onClickAdd={onClickAddNote}
        onClickEdit={onClickEditNote}
        onClickDelete={onClickDeleteNote}
        onProgressUpdate={handleProgressUpdate}
      />
      <Modal
        id="projectDetailsModal"
        title="Add Note"
        body={noteForm}
        close={actions.hideModal}
      />
      <ConfirmModal
        id="noteDeleteModal"
        title="Delete Note"
        body="Are you sure you want to delete this note?"
        close={actions.hideModal}
        confirm={handleDeleteNote}
      />
    </div>
  );
};

ProjectProgressPage.propTypes = {
  auth: PropTypes.object,
  history: PropTypes.object,
  isMobile: PropTypes.bool,
  actions: PropTypes.object,
  projectNoteToDelete: PropTypes.string,
  saving: PropTypes.bool
};
function mapStatesToProps(state) {
  return {
    auth: state.reducers.auth,
    projectNoteToDelete: state.reducers.projectNoteToDelete,
    saving: state.reducers.savingProject
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { ...projectActions, ...modalActions, ...codeActions },
      dispatch
    )
  };
}
export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(ProjectProgressPage);
